import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import AssignCollegeModal from './AssignCollegeModal';
import ForwardToCollegeModal from './ForwardToCollegeModal';
import FundingClaimModal from './FundingClaimModal';
import { useSelection, SelectionCheckbox, BulkActionBar } from './BulkSelect';
import {
  Loader2, Users, Building2, GraduationCap, FileText, Download, Eye, MapPin,
  Mail, Phone, Calendar, Globe, User, Trash2, X, Link2, Send, DollarSign,
} from 'lucide-react';

const STAGES = ['draft', 'submitted', 'applied', 'admitted', 'enrolled', 'rejected'];
const FUNDING_OPTIONS = [
  { value: '', label: '— Set status —' },
  { value: 'applied_aid_bc', label: 'Applied for Student Aid BC' },
  { value: 'funding_received', label: 'Funding Received' },
  { value: 'funding_claimed', label: 'Funding Claimed' },
  { value: 'not_accepted', label: 'Not Accepted' },
];
const fundingLabel = (v) => (FUNDING_OPTIONS.find((o) => o.value === v) || { label: '—' }).label;
const fundingPill = (v) => {
  const map = {
    applied_aid_bc: ['bg-[#DEE9FA]', 'text-[#1e40af]', 'Applied · Aid BC'],
    funding_received: ['bg-[#FEF4DB]', 'text-[#8a5e00]', 'Funding Received'],
    funding_claimed: ['bg-[#E1F3E4]', 'text-[#0B5E2A]', 'Funding Claimed'],
    not_accepted: ['bg-[#FDE2E2]', 'text-[#9b1c1c]', 'Not Accepted'],
  };
  if (!v) return <span className="text-xs text-[#6b7e6d] italic">—</span>;
  const [bg, tx, lbl] = map[v] || ['bg-[#eee]', 'text-[#4b5e4d]', v];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${bg} ${tx}`}>{lbl}</span>;
};

const stagePill = (v) => {
  const map = {
    draft: ['bg-[#eee]', 'text-[#4b5e4d]'],
    submitted: ['bg-[#FEF4DB]', 'text-[#8a5e00]'],
    applied: ['bg-[#DEE9FA]', 'text-[#1e40af]'],
    admitted: ['bg-[#E1F3E4]', 'text-[#0B5E2A]'],
    enrolled: ['bg-[#E1F3E4]', 'text-[#0B5E2A]'],
    rejected: ['bg-[#FDE2E2]', 'text-[#9b1c1c]'],
  };
  const [bg, tx] = map[v] || map.submitted;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${bg} ${tx}`}>{v || 'submitted'}</span>;
};

const StudentsTab = ({ filter }) => {
  const { toast } = useToast();
  const [students, setStudents] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [forwarding, setForwarding] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const stuSel = useSelection();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sR, cR] = await Promise.all([
        api.get('/admin/students'),
        api.get('/admin/colleges'),
      ]);
      setStudents(sR.data); setColleges(cR.data);
    } catch { /* 401 global */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onStageChange = async (s, stage) => {
    try { await api.put(`/admin/students/${s.id}/stage`, { application_stage: stage }); toast({ title: 'Stage updated' }); load(); }
    catch (e) { toast({ title: 'Update failed' }); }
  };

  const handleFundingChange = async (s, newStatus) => {
    if (newStatus === 'funding_claimed') {
      // Open the claims popup; nothing changes until admin fills amounts.
      setClaiming(s);
      return;
    }
    try {
      await api.put(`/admin/students/${s.id}/funding-status`, { funding_status: newStatus });
      toast({ title: 'Funding status updated' });
      load();
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || '' }); }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Move ${s.full_name} to Trash?`)) return;
    try { await api.delete(`/admin/students/${s.id}`); toast({ title: 'Moved to Trash' }); load(); }
    catch { toast({ title: 'Delete failed' }); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(stuSel.selected);
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} student${ids.length === 1 ? '' : 's'} to Trash?`)) return;
    try {
      const { data } = await api.post('/admin/students/bulk-delete', { ids });
      toast({ title: 'Moved to Trash', description: `${data.trashed} item(s) in the Trash tab.` });
      stuSel.clear();
      load();
    } catch (e) { toast({ title: 'Bulk delete failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const downloadDoc = async (s, idx, name) => {
    try {
      const r = await api.get(`/admin/students/${s.id}/documents/${idx}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name || 'document'; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed' }); }
  };

  const q = (filter || '').toLowerCase();
  const filtered = students.filter((s) =>
    !q || (s.full_name || '').toLowerCase().includes(q) ||
    (s.email || '').toLowerCase().includes(q) ||
    (s.nationality || '').toLowerCase().includes(q) ||
    (s.recruiter_name || '').toLowerCase().includes(q) ||
    (s.assigned_college_name || '').toLowerCase().includes(q)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#4b5e4d]">
          <span className="font-semibold text-[#0B5E2A]">{filtered.length}</span> {filtered.length === 1 ? 'student' : 'students'}
        </div>
      </div>

      <BulkActionBar count={stuSel.count} onClear={stuSel.clear} onDelete={bulkDelete} itemType="student" testid="bulk-bar-students" />

      <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[#6b7e6d]"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="h-10 w-10 mx-auto brand-green opacity-60" />
            <div className="mt-3 font-display text-lg text-[#0B5E2A]">No students yet</div>
            <p className="mt-1 text-sm text-[#6b7e6d]">Students submitted by your recruiters will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
                <tr>
                  <Th className="w-10 text-center">
                    <SelectionCheckbox
                      checked={filtered.length > 0 && filtered.every((s) => stuSel.has(s.id))}
                      onChange={() => stuSel.toggleAll(filtered.map((s) => s.id))}
                      testid="stu-select-all"
                      label="Select all students"
                    />
                  </Th>
                  <Th>Student</Th><Th>Recruiter</Th><Th>Assigned College / Program</Th><Th>Funding</Th><Th>Commission</Th><Th>Stage</Th><Th className="text-right pr-6">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const collegeFee = Number(s.college_fee_snapshot || 0);
                  const commission = Number(s.recruiter_commission_snapshot || 0);
                  const net = collegeFee - commission;
                  return (
                    <tr key={s.id} className={`border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee] ${stuSel.has(s.id) ? 'bg-[#f0f6ee]' : ''}`}>
                      <Td className="text-center">
                        <SelectionCheckbox
                          checked={stuSel.has(s.id)}
                          onChange={() => stuSel.toggle(s.id)}
                          testid={`stu-select-${s.id}`}
                          label={`Select ${s.full_name}`}
                        />
                      </Td>
                      <Td>
                        <div className="font-semibold text-[#0B5E2A] cursor-pointer hover:underline" onClick={() => setSelected(s)}>{s.full_name}</div>
                        <div className="text-xs text-[#6b7e6d]">{s.email}</div>
                      </Td>
                      <Td><div className="text-sm">{s.recruiter_name || '—'}</div><div className="text-xs text-[#6b7e6d]">{s.recruiter_email || ''}</div></Td>
                      <Td>
                        {s.assigned_college_name ? (
                          <div>
                            <div className="font-medium flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 brand-green" />{s.assigned_college_name}</div>
                            {s.assigned_program_name && <div className="text-xs text-[#6b7e6d] flex items-center gap-1.5 mt-0.5"><GraduationCap className="h-3 w-3" />{s.assigned_program_name}</div>}
                          </div>
                        ) : (
                          <button onClick={() => setAssigning(s)} className="text-xs text-[#1e40af] font-semibold hover:underline inline-flex items-center gap-1"><Link2 className="h-3 w-3" /> Assign</button>
                        )}
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-1">
                          <select
                            value={s.funding_status || ''}
                            onChange={(e) => handleFundingChange(s, e.target.value)}
                            data-testid={`funding-status-${s.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] px-2 py-1 rounded border border-[#0B5E2A]/20 bg-white focus:outline-none max-w-[180px]"
                          >
                            {FUNDING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          {s.funding_status === 'funding_claimed' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setClaiming(s); }}
                              data-testid={`funding-view-claims-${s.id}`}
                              className="text-[10px] text-[#1e40af] hover:underline inline-flex items-center gap-1 self-start"
                            >
                              <DollarSign className="h-3 w-3" /> Edit claims
                            </button>
                          )}
                        </div>
                      </Td>
                      <Td>
                        {s.assigned_college ? (
                          <div>
                            <div className={`font-bold ${s.funding_status === 'funding_claimed' ? 'text-[#0B5E2A]' : 'text-[#6b7e6d]'}`} data-testid={`admin-student-commission-${s.id}`}>
                              ${Number(s.recruiter_commission_snapshot || 0).toLocaleString()}
                            </div>
                            {s.commission_percent_applied > 0 && (
                              <div className="text-[10px] text-[#6b7e6d]">{s.commission_percent_applied}% rate</div>
                            )}
                            <div className="text-[10px] text-[#6b7e6d]">{s.funding_status === 'funding_claimed' ? 'Paid' : 'Pending'}</div>
                          </div>
                        ) : (<span className="text-xs text-[#6b7e6d] italic">—</span>)}
                      </Td>
                      <Td>
                        <select value={s.application_stage || 'submitted'} onChange={(e) => onStageChange(s, e.target.value)} className="text-xs px-2 py-1.5 rounded border border-[#0B5E2A]/20 bg-white font-semibold capitalize focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                          {STAGES.map((st) => <option key={st} value={st}>{st}</option>)}
                        </select>
                      </Td>
                      <Td className="text-right pr-4">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <button onClick={() => setAssigning(s)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#0B5E2A] text-white hover:bg-[#094a22] transition-colors">
                            <Building2 className="h-3.5 w-3.5" /> {s.assigned_college ? 'Reassign' : 'Assign'}
                          </button>
                          <button
                            onClick={() => setForwarding(s)}
                            disabled={!s.assigned_college}
                            title={s.assigned_college ? 'Forward to college' : 'Assign a college first'}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#F0B93C] text-[#06401d] hover:bg-[#e7a91f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Send className="h-3.5 w-3.5" /> Forward
                          </button>
                          <button onClick={() => setSelected(s)} className="h-7 w-7 rounded text-[#0B5E2A] hover:bg-[#f0f6ee] flex items-center justify-center" title="View"><Eye className="h-3.5 w-3.5" /></button>
                          <button onClick={() => onDelete(s)} className="h-7 w-7 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <StudentDetailDrawer student={selected} onClose={() => setSelected(null)} onDownload={downloadDoc} />}
      {assigning && <AssignCollegeModal student={assigning} colleges={colleges} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); load(); }} />}
      {forwarding && <ForwardToCollegeModal student={forwarding} onClose={() => setForwarding(null)} onSent={() => { setForwarding(null); load(); }} />}
      {claiming && <FundingClaimModal student={claiming} colleges={colleges} onClose={() => setClaiming(null)} onSaved={() => { setClaiming(null); load(); }} />}
    </div>
  );
};

const StudentDetailDrawer = ({ student, onClose, onDownload }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-0 right-0 z-50 w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#0B5E2A]/10 sticky top-0 bg-white z-10 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Student Profile</div>
            <h3 className="font-display text-2xl text-[#0B5E2A]">{student.full_name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]"><X className="h-5 w-5 pointer-events-none" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Row icon={Mail} label="Email" value={student.email} />
          <Row icon={Phone} label="Phone" value={`${student.phone_code} ${student.phone}`} />
          <Row icon={Calendar} label="Date of Birth" value={student.dob} />
          <Row icon={User} label="Gender" value={student.gender || '—'} />
          <Row icon={Globe} label="Nationality" value={student.nationality} />
          <Row icon={MapPin} label="Address" value={student.address || '—'} />
          <Row icon={Globe} label="Status in Canada" value={student.passport_pr_status} />
          <Row icon={User} label="Submitted By" value={student.recruiter_name || 'Direct'} />
          {student.assigned_college_name && <Row icon={Building2} label="Assigned College" value={student.assigned_college_name} />}
          {student.assigned_program_name && <Row icon={GraduationCap} label="Assigned Program" value={student.assigned_program_name} />}
          {student.assigned_college && (student.college_fee_snapshot > 0 || student.recruiter_commission_snapshot > 0) && (
            <div className="rounded-lg border border-[#F0B93C]/40 bg-[#FEF8E6] p-3">
              <div className="text-xs font-semibold text-[#8a5e00] uppercase tracking-wider mb-2">Pricing (admin only)</div>
              <div className="space-y-0 bg-white rounded-md border border-[#F0B93C]/30 overflow-hidden">
                <div className="px-3 py-2 border-b border-[#F0B93C]/20 flex items-center justify-between text-sm">
                  <span className="text-[#6b7e6d]">College Fee</span>
                  <span className="font-semibold">${Number(student.college_fee_snapshot || 0).toLocaleString()}</span>
                </div>
                <div className="px-3 py-2 border-b border-[#F0B93C]/20 flex items-center justify-between text-sm">
                  <span className="text-[#6b7e6d]">− Recruiter Commission</span>
                  <span className="font-semibold">${Number(student.recruiter_commission_snapshot || 0).toLocaleString()}</span>
                </div>
                <div className="bg-[#0B5E2A] text-white px-3 py-2 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider font-semibold">= HCC Revenue</span>
                  <span className="font-display text-lg font-bold">${(Number(student.college_fee_snapshot || 0) - Number(student.recruiter_commission_snapshot || 0)).toLocaleString()}</span>
                </div>
              </div>
              <div className="mt-2 text-[10px] text-[#8a5e00]/80">
                Status: <span className="font-semibold">{student.application_stage === 'enrolled' ? 'Commission paid on enrollment' : 'Commission pending enrollment'}</span>
              </div>
            </div>
          )}
          {student.notes && (
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold mb-1">Notes</div>
              <div className="text-sm text-[#1b2a1f] whitespace-pre-wrap bg-[#f6faf3] rounded-md p-3 border border-[#0B5E2A]/10">{student.notes}</div>
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold mb-2">Documents ({(student.documents || []).length})</div>
            {(student.documents || []).length === 0 ? (
              <div className="text-sm text-[#6b7e6d] italic">No documents.</div>
            ) : (
              <ul className="space-y-2">
                {(student.documents || []).map((d, i) => (
                  <li key={i} className="flex items-center gap-3 bg-[#f6faf3] border border-[#0B5E2A]/10 rounded-md px-3 py-2.5">
                    <FileText className="h-4 w-4 brand-green" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium">{d.name}</div>
                      <div className="text-xs text-[#6b7e6d]">{d.doc_type} · {Math.ceil((d.size || 0)/1024)} KB</div>
                    </div>
                    <button onClick={() => onDownload(student, i, d.name)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">
                      <Download className="h-3.5 w-3.5 pointer-events-none" /> Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Th = ({ children, className = '' }) => <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="h-8 w-8 rounded-md bg-[#f0f6ee] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 brand-green" /></div>
    <div><div className="text-[11px] uppercase tracking-wider text-[#6b7e6d] font-semibold">{label}</div><div className="text-sm text-[#1b2a1f]">{value}</div></div>
  </div>
);

export default StudentsTab;
